// frontend/src/hooks/useColumnTypes.js
// AG-Grid column types with formatters baked in.
// Returns the full `columnTypes` object to pass as <AgGridReact columnTypes={...} />.
//
// For currency columns or columns needing a non-default format,
// add a valueFormatter directly on the column — it overrides the type's formatter.

import { useMemo } from 'react';
import { useFormatters } from './useFormatters';
import DateCellEditor from '../components/common/DateCellEditor';
import { isEmptySummaryCell } from '../utils/pinnedTotals';

// Alignment as cellClassRules, not a cellClass string: AG Grid replaces a
// column's cellClass but merges its cellClassRules with the type's, so a column
// that adds its own class (a report's group tint) keeps the type's alignment,
// and `type: ['numberColumn', 'centeredColumn']` lets the later type win.
const RIGHT_ALIGNED = { 'ag-right-aligned-cell': () => true };
const CENTERED = { 'ag-center-aligned-cell': () => true, 'ag-right-aligned-cell': () => false };

export function useColumnTypes() {
  const { formatNumber, formatDate, formatDateTime, parseNumber } = useFormatters();

  return useMemo(() => ({

    // Integer quantities — no decimal places (inventory, forecast, PO quantities, etc.)
    numberColumn: {
      cellDataType: 'number',
      cellEditor: 'agTextCellEditor',
      // Seed the editor input with the formatted display value (e.g. "1.234"
      // in EU locale) instead of the raw number — otherwise double-clicking
      // a cell shows the bare float and the user's locale looks broken.
      // valueParser below converts it back on save.
      cellEditorParams: { useFormatter: true },
      headerClass: 'ag-right-aligned-header',
      cellClassRules: RIGHT_ALIGNED,
      comparator: (a, b) => parseFloat(a ?? 0) - parseFloat(b ?? 0),
      valueFormatter: (params) => {
        // A totals or product row carries only the fields that sum sensibly
        // (not per-unit prices, MOQ, ratios), so the lookup can yield undefined.
        if (isEmptySummaryCell(params)) return '';
        // A missing figure is a dash, never a substituted 0 — the same rule
        // decimalColumn already applied; a 0 reads as a measured zero.
        return params.value == null ? '—' : formatNumber(params.value, 0);
      },
      valueParser: (params) => {
        if (params.newValue === '' || params.newValue == null) return null;
        const parsed = Math.round(parseNumber(params.newValue));
        return isNaN(parsed) ? params.oldValue : parsed;
      },
    },

    // Decimal numbers — 2 decimal places (prices, rates, etc.)
    decimalColumn: {
      cellDataType: 'number',
      cellEditor: 'agTextCellEditor',
      // See note on numberColumn — needed so "3,50" (EU) or "3.50" (US)
      // shows up identically in the editor as in the cell display.
      cellEditorParams: { useFormatter: true },
      headerClass: 'ag-right-aligned-header',
      cellClassRules: RIGHT_ALIGNED,
      comparator: (a, b) => parseFloat(a ?? 0) - parseFloat(b ?? 0),
      valueFormatter: (params) => {
        // See numberColumn.
        if (isEmptySummaryCell(params)) return '';
        return params.value == null ? '—' : formatNumber(params.value, 2);
      },
      valueParser: (params) => {
        if (params.newValue === '' || params.newValue == null) return null;
        const parsed = parseNumber(params.newValue);
        return isNaN(parsed) ? params.oldValue : parsed;
      },
    },

    // Calendar date — user's preferred date format (no time). Where a column
    // is editable, the day is picked in the app's date picker; the column's
    // cellEditorParams may bound it with minDate / maxDate ('YYYY-MM-DD').
    dateColumn: {
      cellEditor: DateCellEditor,
      headerClass: 'ag-center-aligned-header',
      cellClassRules: CENTERED,
      valueFormatter: (params) => (params.value ? formatDate(params.value) : ''),
    },

    // Full timestamp — user's preferred date+time format + timezone
    dateTimeColumn: {
      headerClass: 'ag-center-aligned-header',
      cellClassRules: CENTERED,
      valueFormatter: (params) => (params.value ? formatDateTime(params.value) : ''),
    },

    // A class letter, a status chip, a flag: centred, no formatter of its own.
    centeredColumn: {
      headerClass: 'ag-center-aligned-header',
      cellClassRules: CENTERED,
    },

    // Boolean — AG Grid's own checkbox. A click writes the value where the
    // column's `editable` allows it, and the checkbox is greyed where it does
    // not. The parser keeps the old value for anything but true/false, so the
    // Delete key (AG Grid writes the parse of '') never empties a flag.
    booleanColumn: {
      cellDataType: 'boolean',
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      editable: true,
      headerClass: 'ag-center-aligned-header',
      cellClassRules: CENTERED,
      valueParser: (params) => {
        const text = String(params.newValue).trim().toLowerCase();
        if (text === 'true') return true;
        if (text === 'false') return false;
        return params.oldValue;
      },
    },

    // A value that is legitimately absent for some rows (a SKU an item has
    // never had, a supplier not yet assigned). The row keeps the wire's null;
    // absence is a display decision, and this is where it is made — once, so
    // the dash cannot drift into a blank cell on one grid and a dash on the
    // next.
    optionalTextColumn: {
      valueFormatter: (params) => {
        if (isEmptySummaryCell(params)) return '';
        return params.value || '-';
      },
    },

  }), [formatNumber, formatDate, formatDateTime, parseNumber]);
}
